/**
 * iter161-tabs — Files & Photos tab views for the Team-Chat pane.
 *
 * Both views walk the `messages` array in-memory (already fetched by
 * `loadMessages(active)`), flatten each message's attachments, and
 * render a filtered slice:
 *   FilesView  → attachments where content_type !startsWith("image/")
 *   PhotosView → attachments where content_type startsWith("image/")
 *
 * Rendering delegates to the existing `<AttachmentChip>` (iter157/158),
 * which already:
 *   • fetches the blob via `GET /team-chat/files/{path}` with existing
 *     access controls,
 *   • opens `AttachmentPreviewModal` on click (never auto-downloads),
 *   • exposes an explicit Download icon on file chips,
 *   • preserves forwarding via the surrounding message context.
 *
 * So these views intentionally contain ZERO fetch / preview / download
 * / forward logic — every one of those flows still runs through the
 * same code path that Chat-tab messages already use.
 *
 * Data sourcing: reuses the existing in-memory `messages` state, no
 * extra network round-trips, no duplicate storage.
 */
import AttachmentChip from "./AttachmentChip";

function isImageAtt(att) {
  return (
    (att.content_type && att.content_type.startsWith("image/")) ||
    att.kind === "image"
  );
}

function collectAttachments(messages, wantImages) {
  const rows = [];
  for (const m of messages || []) {
    if (m.deleted_for_all) continue;
    for (const a of m.attachments || []) {
      const img = isImageAtt(a);
      if (wantImages ? img : !img) rows.push({ msg: m, att: a });
    }
  }
  // Most recent first, matches Files/Photos gallery convention.
  rows.sort((a, b) => new Date(b.msg.created_at) - new Date(a.msg.created_at));
  return rows;
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleString([], {
      day: "numeric",
      month: "short",
      ...(sameYear ? {} : { year: "numeric" }),
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return ""; }
}

export function FilesView({ messages }) {
  const rows = collectAttachments(messages, /*wantImages=*/ false);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-white p-4" data-testid="team-chat-files-view">
      {rows.length === 0 ? (
        <div className="text-center text-[var(--text-muted)] py-16 text-[13px]" data-testid="team-chat-files-empty">
          No files shared yet
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-2">
          {rows.map(({ msg, att }, i) => (
            <div
              key={`${msg.id}-${att.path}-${i}`}
              className="flex items-start gap-3 p-3 border border-[var(--border)] rounded-lg bg-white hover:bg-[var(--bg-sidebar)] transition-colors"
              data-testid={`team-chat-file-row-${att.path}`}
            >
              <div className="flex-1 min-w-0">
                <AttachmentChip att={att} msg={msg} mine={false} />
                <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                  Shared by {msg.from_user_name || "someone"} · {formatWhen(msg.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PhotosView({ messages }) {
  const rows = collectAttachments(messages, /*wantImages=*/ true);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-white p-4" data-testid="team-chat-photos-view">
      {rows.length === 0 ? (
        <div className="text-center text-[var(--text-muted)] py-16 text-[13px]" data-testid="team-chat-photos-empty">
          No photos shared yet
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {rows.map(({ msg, att }, i) => (
            <div
              key={`${msg.id}-${att.path}-${i}`}
              className="relative rounded-lg overflow-hidden border border-[var(--border)] bg-[var(--bg-sidebar)] aspect-square group"
              data-testid={`team-chat-photo-cell-${att.path}`}
            >
              <div className="absolute inset-0 flex items-center justify-center [&_button]:!block [&_button]:!w-full [&_button]:!h-full [&_img]:!w-full [&_img]:!h-full [&_img]:!max-w-none [&_img]:!max-h-none [&_img]:!object-cover">
                <AttachmentChip att={att} msg={msg} mine={false} />
              </div>
              <div className="pointer-events-none absolute bottom-0 inset-x-0 px-2 py-1 bg-gradient-to-t from-black/60 to-transparent text-white text-[10px] font-medium truncate opacity-0 group-hover:opacity-100 transition-opacity">
                {msg.from_user_name || "someone"} · {formatWhen(msg.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
